import {
  Button,
  DatePicker,
  Form,
  FormContent,
  Input,
  message,
  Radio,
  Select,
  useBreadcrumbRoutes,
  InputNumber,
  Wrapper,
  Upload,
  BaseInfoContent,
  getRealUrl,
} from '@maxtropy/components';
import { Cascader, Space } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';

import {
  apiV2CustomerBillCheckBillNamePost,
  apiV2CustomerBillCheckBillUniquePost,
  apiV2CustomerBillCreatePost,
  apiV2CustomerBillDetailPost,
  apiV2CustomerBillUpdatePost,
  apiV2OutputProductBaseLinePhysicalUnitPost,
} from '@maxtropy/device-customer-apis-v2';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { BillItem, dataSource, getBillContentColumns, getNumberRules, Sum } from '../utils';
import { energyMediumSceneToMediumRes, getEnergyMediumSceneToMedium } from '@/api/workingSubstance';
import dayjs from 'dayjs';
import style from '../index.module.scss';

const HistoryBillDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const mode = searchParams.get('mode');
  const isView = mode === 'view';
  const isEdit = mode === 'edit';
  const [form] = Form.useForm();
  const waterQuantity = Form.useWatch('waterQuantity', form) || '';
  const waterPrice = Form.useWatch('waterPrice', form) || '';
  const standard = Form.useWatch('standard', form) || '';
  const coefficient = Form.useWatch('coefficient', form) || '';
  const currentUnitId = Form.useWatch('physicalUnitId', form);

  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [unitOptions, setUnitOptions] = useState<any[]>([]);

  const [fileKey, setFileKey] = useState<string>();
  const [EnergyMediumId, setEnergyMediumId] = useState<number>();
  const [energyMediumSceneToMedium, setEnergyMediumSceneToMedium] = useState<energyMediumSceneToMediumRes[]>([]);
  const waterFeeDisplay = useMemo(() => {
    if (waterQuantity && waterPrice) {
      return (waterQuantity * waterPrice).toFixed(2);
    }
  }, [waterQuantity, waterPrice]);

  const sewageFeeDisplay = useMemo(() => {
    if (waterQuantity && standard && coefficient) {
      return (waterQuantity * standard * coefficient).toFixed(2);
    }
  }, [waterQuantity, standard, coefficient]);

  //根据能源介质获取单位
  const loadUnitsByMediumId = async (mediumId: number, targetUnitId?: number) => {
    if (!mediumId) return;
    try {
      const res = await apiV2OutputProductBaseLinePhysicalUnitPost({ id: mediumId });
      const list = res.list || [];
      const options = list.map(item => ({
        label: item.physicalUnitName,
        value: item.physicalUnitId,
      }));

      const finalUnitId = targetUnitId !== undefined ? targetUnitId : options[0]?.value;
      setUnitOptions(options);
      if (finalUnitId) {
        form.setFieldsValue({
          physicalUnitId: finalUnitId,
        });
      }
    } catch (error) {
      console.error('获取单位失败', error);
      setUnitOptions([]);
    }
  };

  //获取能源介质
  useEffect(() => {
    getEnergyMediumSceneToMedium().then(setEnergyMediumSceneToMedium);
  }, []);
  const energyMediumSceneToMediumOptions = useMemo(() => {
    return energyMediumSceneToMedium.map(i => ({
      label: i.sceneName,
      value: i.id,
      children: i.energyMediums.map(item => ({
        label: item.energyName,
        value: item.id,
        isLeaf: true,
      })),
    }));
  }, [energyMediumSceneToMedium]);

  //回填
  useEffect(() => {
    if (id) {
      apiV2CustomerBillDetailPost({ id: Number(id) }).then(res => {
        let foundPath: number[] | null = null;
        energyMediumSceneToMediumOptions.find(scene => {
          const hasMedium = scene.children?.some(medium => medium.value === res.energyMediumId);
          if (hasMedium && res.energyMediumId) {
            foundPath = [scene.value, res.energyMediumId];
            setEnergyMediumId(res.energyMediumId);
            return true;
          }
          return false;
        });

        if (foundPath && res.energyMediumId) {
          const { energyMediumId, physicalUnitId, billMonth, ...restFields } = res;

          form.setFieldsValue({
            ...restFields,
            energyMediumId: foundPath,
            billMonth: dayjs(billMonth),
          });
          loadUnitsByMediumId(res.energyMediumId, res.physicalUnitId);

          if (isView) {
            setFileKey(restFields.fileKey);
          }
        }
      });
    }
  }, [id, energyMediumSceneToMediumOptions]);

  //根据能源介质获取单位
  const handleMediaChange = (value: number[], selectedOptions: any[]) => {
    if (!selectedOptions || selectedOptions.length === 0) {
      form.setFieldsValue({ physicalUnitId: undefined });
      setUnitOptions([]);
      return;
    }
    const mediumId = value[value.length - 1];
    setEnergyMediumId(mediumId);
    loadUnitsByMediumId(mediumId);
  };

  // 监听表单值变化进行自动计算

  const onFinish = async (values: any) => {
    const payload = {
      ...values,
      energyMediumId: values.energyMediumId[values.energyMediumId.length - 1],
      billMonth: dayjs(values.billMonth).startOf('month').valueOf(),
    };

    if (isEdit) {
      apiV2CustomerBillUpdatePost({ id: Number(id), ...payload }).then(() => {
        navigate('/historybill/bills/import/');
      });
    } else {
      const billCheckBillNameParams = {
        name: values.billName,
        ...(isEdit ? { billId: id } : {}),
      };
      const nameCheckRes = await apiV2CustomerBillCheckBillNamePost(billCheckBillNameParams);
      if (nameCheckRes.flag) {
        message.error('名称重复');
        return;
      }
      const billCheckUniqueParams = {
        billMonth: dayjs(values.billMonth).startOf('month').valueOf(),
        energyMediumId: values.energyMediumId[values.energyMediumId.length - 1],
        billType: values.billType,
        ...(isEdit ? { billId: id } : {}),
      };
      const billCheckRes = await apiV2CustomerBillCheckBillUniquePost(billCheckUniqueParams);
      if (billCheckRes.flag) {
        message.error('同月同类型账单已存在');
        return;
      }
      apiV2CustomerBillCreatePost(payload).then(() => {
        navigate('/historybill/bills/import/');
      });
    }
  };
  const renderContent = () => {
    const currentUnitName = unitOptions.find(option => option.value === currentUnitId)?.label;
    if (EnergyMediumId === 2001) {
      return (
        <>
          <div className={style.waterWrapper}>
            <div className={style.waterAmountWrapper}>
              <span>供水费:</span>
              <span className={style.waterAmount}>{waterFeeDisplay ?? '-'}元</span>
            </div>
            <div className={style.waterFormWrapper}>
              <div className={style.waterFormItem}>
                <Form.Item
                  name="waterQuantity"
                  label="本月用水量"
                  rules={getNumberRules('small')}
                  style={{ marginBottom: 0 }}
                >
                  <InputNumber placeholder="请输入" addonAfter={currentUnitName} style={{ width: '100%' }} />
                </Form.Item>
              </div>

              <div className={style.waterFormItem}>
                <Form.Item
                  name="waterPrice"
                  label="自来水单价"
                  rules={getNumberRules('small')}
                  style={{ marginBottom: 0 }}
                >
                  <InputNumber placeholder="请输入" addonAfter={`元/${currentUnitName}`} style={{ width: '100%' }} />
                </Form.Item>
              </div>
              <div className={style.waterFormItem}></div>
            </div>
          </div>
          {/* --- 代征污水处理费部分 --- */}
          <div className={style.waterWrapper}>
            <div className={style.waterAmountWrapper}>
              <span>代征污水处理费:</span>
              <span className={style.waterAmount}>{sewageFeeDisplay ?? '-'}元</span>
            </div>
            <div className={style.waterFormWrapper}>
              <div className={style.waterFormItem}>
                <div className={style.waterUsage}>
                  <span style={{ color: '#f6373bff', marginRight: 4 }}>*</span>本月用水量
                </div>
                <div>
                  {waterQuantity ? `${parseFloat(waterQuantity).toFixed(2)}` : '-'} {currentUnitName || 'm³'}
                </div>
              </div>
              <div className={style.waterFormItem}>
                <Form.Item name="standard" label="征收标准" rules={getNumberRules('small')} style={{ marginBottom: 0 }}>
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="请输入"
                    addonAfter={`元/${currentUnitName}` || '-'}
                  />
                </Form.Item>
              </div>
              <div className={style.waterFormItem}>
                <Form.Item
                  name="coefficient"
                  label="系数"
                  initialValue={0.9}
                  rules={getNumberRules('small')}
                  style={{ marginBottom: 10 }}
                  tooltip={{
                    title: '代征污水处理费计算方式：用水量 * 征收标准 * 系数',
                    icon: <InfoCircleOutlined style={{ color: '#FAAD14 ' }} />,
                  }}
                >
                  <InputNumber style={{ width: '100%' }} placeholder="0.90" precision={2} />
                </Form.Item>
              </div>
            </div>
          </div>
          <div className={style.totalAmountWrapper}>
            <div className={style.totalAmountItem}>
              <div>本月水费</div>
              <div className={style.waterAmount}>
                {sewageFeeDisplay && waterFeeDisplay
                  ? (parseFloat(sewageFeeDisplay) + parseFloat(waterFeeDisplay)).toFixed(2)
                  : '-'}
                元
              </div>
            </div>
            <div className={style.totalAmountItem}>
              <div>本月用水量</div>
              <div className={style.waterAmount}>
                {(waterQuantity && parseFloat(waterQuantity).toFixed(2)) || '-'}
                {currentUnitName || 'm³'}
              </div>
            </div>
          </div>
        </>
      );
    }
    return (
      <>
        <Form.Item name="usage" label="本月用量" rules={getNumberRules('small')}>
          <InputNumber addonAfter={currentUnitName} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="cost" label="本月成本" rules={getNumberRules('small')}>
          <InputNumber addonAfter={'元'} style={{ width: '100%' }} />
        </Form.Item>
        {isView ? (
          fileKey && (
            <Button
              type="link"
              disabled={false}
              onClick={() => {
                window.open(getRealUrl(fileKey));
              }}
            >
              下载附件
            </Button>
          )
        ) : (
          <Form.Item name="fileKey" label="附件上传">
            <Upload listType="text" accept={['.xlsx', '.xls']} uploadText="上传文件" fileSize={20} />
          </Form.Item>
        )}
      </>
    );
  };

  return (
    <Wrapper
      routes={[...(breadcrumbRoutes?.routes ?? []), { name: isEdit ? '编辑账单' : isView ? '查看账单' : '新增账单' }]}
    >
      <Form form={form} disabled={isView} onFinish={onFinish} layout="vertical">
        <FormContent narrow title={isEdit ? '编辑账单' : isView ? '查看账单' : '新增账单'}>
          <BaseInfoContent>
            <Form.Item
              name="billName"
              label="账单名称"
              rules={[
                { required: true, message: '请输入账单名称' },
                { min: 1, max: 50, message: '账单名称长度为1-50个字符' },
              ]}
            >
              <Input placeholder="请输入账单名称" disabled={isView} maxLength={50} />
            </Form.Item>

            <Form.Item name="billMonth" label="账单月份" rules={[{ required: true, message: '请选择账单名称' }]}>
              <DatePicker
                picker="month"
                placeholder="请选择账单月份"
                disabled={isView || isEdit}
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item name="energyMediumId" label="能源介质" rules={[{ required: true, message: '请选择能源介质' }]}>
              <Cascader
                disabled={isView || isEdit}
                options={energyMediumSceneToMediumOptions}
                fieldNames={{
                  label: 'label',
                  value: 'value',
                  children: 'children',
                }}
                placeholder="请选择"
                onChange={handleMediaChange}
                allowClear
                showSearch={{
                  filter: (inputValue: string, path: any[]) =>
                    path.some(option => (option.label as string).toLowerCase().indexOf(inputValue.toLowerCase()) > -1),
                }}
              />
            </Form.Item>

            <Form.Item name="physicalUnitId" label="介质单位" rules={[{ required: true, message: '请选择介质单位' }]}>
              <Select placeholder="请选择单位" allowClear options={unitOptions} disabled={isView || isEdit} />
            </Form.Item>

            <Form.Item name="billType" label="账单类型" rules={[{ required: true, message: '请选择账单类型' }]}>
              <Radio.Group>
                <Radio value={1}>支出</Radio>
              </Radio.Group>
            </Form.Item>
          </BaseInfoContent>
          <BaseInfoContent title="账单内容" className={style.billContent}>
            {renderContent()}
          </BaseInfoContent>
          {EnergyMediumId === 2001 &&
            (isView ? (
              fileKey && (
                <Button
                  type="link"
                  disabled={false}
                  onClick={() => {
                    window.open(getRealUrl(fileKey));
                  }}
                >
                  下载附件
                </Button>
              )
            ) : (
              <Form.Item name="fileKey" label="附件上传">
                <Upload listType="text" accept={['.xlsx', '.xls']} uploadText="上传文件" fileSize={20} />
              </Form.Item>
            ))}
          {!isView && (
            <Space size="small">
              <Button type="primary" onClick={() => form.submit()}>
                保存
              </Button>
              <Button type="default" onClick={() => navigate('/historybill/bills/import/')}>
                取消
              </Button>
            </Space>
          )}
        </FormContent>
      </Form>
      {isView && (
        <Button type="primary" onClick={() => navigate('/historybill/bills/import/')}>
          返回
        </Button>
      )}
    </Wrapper>
  );
};
export default HistoryBillDetail;
